/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useState } from "react";
import { 
  Zap, 
  Lock, 
  Settings, 
  ArrowRight, 
  Menu,
  X
} from "lucide-react";
import { cn } from "../utils/cn";

interface FileUploadProps {
  onSpecLoaded?: (spec: any) => void;
  isLoggedIn?: boolean;
  onLoginClick: () => void;
  onNavigate?: (page: "home" | "privacy" | "terms" | "contact" | "documentation") => void;
}

const trustedCompanies = [
  { name: "DEVTOOLS", style: "text-zinc-400 font-medium" },
  { name: "CODE_BASE", style: "text-zinc-400 font-medium" },
  { name: "WEB_API", style: "text-zinc-400 font-medium" },
  { name: "FAST_API", style: "text-zinc-400 font-medium border border-zinc-300 px-4 py-2" },
];

const features = [
  {
    icon: Zap,
    title: "INSTANT PREVIEW",
    description: "Upload your spec and see the documentation live in milliseconds. No deployment steps, no waiting, just instant clarity."
  },
  {
    icon: Lock,
    title: "SECURE ACCESS",
    description: "Enterprise-grade authentication. Control who sees your documentation with API keys, Bearer tokens, or OAuth2 integration."
  },
  {
    icon: Settings,
    title: "EASY INTEGRATION",
    description: "Sync directly with your CI/CD pipeline. Automatically update documentation every time you push code to production."
  }
];

export function FileUpload({ onSpecLoaded: _, isLoggedIn: __, onLoginClick, onNavigate }: FileUploadProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    element?.scrollIntoView({ behavior: "smooth" });
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-zinc-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <img src="/docgether.png" alt="DocGether" className="h-10 w-auto rounded-lg" />
            </div>

            {/* Desktop Nav Links */}
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection("features")} className="text-xs font-medium text-zinc-500 hover:text-zinc-900 tracking-wider transition-colors">
                FEATURES
              </button>
              <button onClick={() => scrollToSection("ai-section")} className="text-xs font-medium text-zinc-500 hover:text-zinc-900 tracking-wider transition-colors">
                AI ENGINE
              </button>
              <button 
                onClick={() => onNavigate?.("documentation")}
                className="text-xs font-medium text-zinc-500 hover:text-zinc-900 tracking-wider transition-colors"
              >
                DOCS
              </button>
              <button className="text-xs font-medium text-zinc-500 hover:text-zinc-900 tracking-wider transition-colors line-through">
                PRICING
              </button>
              <span className="text-xs font-bold text-emerald-600 tracking-wider">FREE</span>
            </div>

            {/* Desktop Auth Buttons */}
            <div className="hidden md:flex items-center gap-4">
              <button onClick={onLoginClick} className="text-xs font-medium text-zinc-900 tracking-wider hover:text-zinc-600 transition-colors">
                SIGN IN
              </button>
              <button 
                onClick={onLoginClick}
                className="bg-zinc-900 text-white text-xs font-medium px-4 py-2 tracking-wider hover:bg-zinc-800 transition-colors"
              >
                GET STARTED
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-zinc-100 px-4 py-4 space-y-4">
            <button onClick={() => scrollToSection("features")} className="block text-sm font-medium text-zinc-600 w-full text-left">
              FEATURES
            </button>
            <button onClick={() => scrollToSection("ai-section")} className="block text-sm font-medium text-zinc-600 w-full text-left">
              AI ENGINE
            </button>
            <button 
              onClick={() => onNavigate?.("documentation")}
              className="block text-sm font-medium text-zinc-600 w-full text-left"
            >
              DOCS
            </button>
            <button className="block text-sm font-medium text-zinc-400 w-full text-left line-through">
              PRICING
            </button>
            <span className="block text-sm font-bold text-emerald-600">FREE</span>
            <div className="pt-4 border-t border-zinc-100 flex flex-col gap-3">
              <button onClick={onLoginClick} className="text-sm font-medium text-zinc-900 w-full text-left">
                SIGN IN
              </button>
              <button 
                onClick={onLoginClick}
                className="bg-zinc-900 text-white text-sm font-medium px-4 py-2 w-full"
              >
                GET STARTED
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-16 lg:pt-32 lg:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left - Content */}
            <div>
              {/* Version Badge */}
              <div className="inline-flex items-center border border-zinc-900 px-3 py-1.5 mb-6">
                <span className="text-[10px] font-semibold text-zinc-900 tracking-widest">AVAILABLE NOW: V3.0</span>
              </div>

              {/* Headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-zinc-900 leading-[1.1] tracking-tight mb-6">
                API<br />
                DOCUMENTATION,<br />
                REIMAGINED.
              </h1>

              {/* Subtext */}
              <p className="text-zinc-500 text-base sm:text-lg leading-relaxed mb-8 max-w-lg">
                Upload your OpenAPI or Swagger specification to instantly generate a beautiful, interactive, and easy-to-navigate documentation interface.
              </p>

              {/* CTA */}
              <div className="mb-8">
                <button
                  onClick={onLoginClick}
                  className="bg-zinc-900 text-white px-6 py-3 text-xs font-semibold tracking-wider hover:bg-zinc-800 transition-colors"
                >
                  GET STARTED FOR FREE
                </button>
              </div>

              {/* Supported Formats */}
              <div className="flex flex-wrap items-center gap-4 text-[10px] text-zinc-400 tracking-widest">
                <span>OPENAPI 3.0+</span>
                <span>SWAGGER 2.0</span>
                <span>JSON & YAML</span>
              </div>
            </div>

            {/* Right - Code Preview Mockup */}
            <div className="relative">
              {/* Main Code Window */}
              <div className="bg-white border border-zinc-200 p-6 sm:p-8 font-mono text-xs sm:text-sm relative">
                {/* Top Bar */}
                <div className="flex items-center justify-between mb-8">
                  {/* Window Controls */}
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 bg-zinc-200"></div>
                    <div className="w-2 h-2 bg-zinc-200"></div>
                    <div className="w-2 h-2 bg-zinc-200"></div>
                  </div>
                  {/* URL Bar */}
                  <div className="w-24 h-2 bg-zinc-100"></div>
                </div>

                {/* Code Content with Line Numbers */}
                <div className="space-y-0.5">
                  <div className="flex">
                    <span className="text-zinc-300 w-6 select-none">01</span>
                    <span className="text-zinc-900 ml-4">"openapi": "3.0.0",</span>
                  </div>
                  <div className="flex">
                    <span className="text-zinc-300 w-6 select-none"></span>
                    <span className="text-zinc-900 ml-4">"info": {'{'}</span>
                  </div>
                  <div className="flex">
                    <span className="text-zinc-300 w-6 select-none"></span>
                    <span className="text-zinc-900 ml-4 ml-8">"title": "CodeGether",</span>
                  </div>
                  <div className="flex">
                    <span className="text-zinc-300 w-6 select-none"></span>
                    <span className="text-zinc-900 ml-4 ml-8">"version": "1.4.2"</span>
                  </div>
                  <div className="flex">
                    <span className="text-zinc-300 w-6 select-none"></span>
                    <span className="text-zinc-900 ml-4">{'}'}</span>
                  </div>
                  <div className="flex">
                    <span className="text-zinc-300 w-6 select-none">06</span>
                    <span className="text-zinc-900 ml-4">"paths": {'{'}</span>
                  </div>
                  <div className="flex">
                    <span className="text-zinc-300 w-6 select-none"></span>
                    <span className="text-zinc-900 ml-4 ml-8">"/why": {'{'}</span>
                  </div>
                  <div className="flex">
                    <span className="text-zinc-300 w-6 select-none"></span>
                    <span className="text-zinc-900 ml-4 ml-12">"get": {'{'}</span>
                  </div>
                  <div className="flex">
                    <span className="text-zinc-300 w-6 select-none"></span>
                    <span className="text-zinc-900 ml-4 ml-16">"summary": "Simple, Lightweight And Efficient"</span>
                  </div>
                  <div className="flex">
                    <span className="text-zinc-300 w-6 select-none"></span>
                    <span className="text-zinc-900 ml-4 ml-12">{'}'}</span>
                  </div>
                  <div className="flex">
                    <span className="text-zinc-300 w-6 select-none"></span>
                    <span className="text-zinc-900 ml-4 ml-8">{'}'}</span>
                  </div>
                  <div className="flex">
                    <span className="text-zinc-300 w-6 select-none"></span>
                    <span className="text-zinc-900 ml-4">{'}'}</span>
                  </div>
                </div>
              </div>

              {/* Render Time Badge - positioned outside/overlapping */}
              <div className="absolute -bottom-6 right-4 sm:right-8 bg-zinc-900 text-white px-5 py-4 shadow-2xl">
                <div className="text-[10px] tracking-wider text-zinc-400 mb-1">RENDER TIME</div>
                <div className="text-3xl font-bold">0.42s</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges Section */}
      <section className="border-y border-zinc-100 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-12">
            <span className="text-[10px] font-semibold text-zinc-400 tracking-widest">TRUSTED BY:</span>
            <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-12">
              {trustedCompanies.map((company) => (
                <span key={company.name} className={cn("text-sm tracking-wider", company.style)}>
                  {company.name}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {/* Section Header */}
          <div className="mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-zinc-900 leading-tight tracking-tight max-w-md">
              EVERYTHING YOU NEED FOR BEAUTIFUL API DOCS
            </h2>
            <div className="w-16 h-1 bg-zinc-900 mt-6"></div>
          </div>

          {/* Feature Cards */}
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {features.map((feature) => (
              <div key={feature.title} className="group border border-zinc-900 p-6 lg:p-8">
                <feature.icon className="w-6 h-6 text-zinc-900 mb-6" strokeWidth={1.5} />
                <h3 className="text-xs font-bold text-zinc-900 tracking-wider mb-4">
                  {feature.title}
                </h3>
                <p className="text-sm text-zinc-500 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Section (Dark) */}
      <section id="ai-section" className="bg-zinc-900 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left - Content */}
            <div>
              <span className="text-[10px] font-semibold text-zinc-500 tracking-widest mb-4 block">
                INTELLIGENCE INTEGRATED
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight tracking-tight mb-6">
                SUPERCHARGE YOUR DOCS WITH AI.
              </h2>
              <p className="text-zinc-400 text-base sm:text-lg leading-relaxed mb-8">
                Our intelligent assistant helps you understand, explain, and work with your APIs like never before. Get contextual code examples and natural language explanations for complex endpoints.
              </p>

              {/* Feature List */}
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-white mt-2"></div>
                  <div>
                    <h4 className="text-xs font-bold text-white tracking-wider mb-1">SMART EXPLANATIONS</h4>
                    <p className="text-sm text-zinc-500">Get natural language explanations for any endpoint, parameter, and response code with one click.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-white mt-2"></div>
                  <div>
                    <h4 className="text-xs font-bold text-white tracking-wider mb-1">AI CHAT ASSISTANT</h4>
                    <p className="text-sm text-zinc-500">Ask questions about your API, find the right endpoints, and get contextual help in real-time.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-white mt-2"></div>
                  <div>
                    <h4 className="text-xs font-bold text-white tracking-wider mb-1">CODE EXAMPLES</h4>
                    <p className="text-sm text-zinc-500">Generate ready-to-use code snippets for any endpoint in multiple programming languages.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-white mt-2"></div>
                  <div>
                    <h4 className="text-xs font-bold text-white tracking-wider mb-1">API DISCOVERY</h4>
                    <p className="text-sm text-zinc-500">Let AI help you discover and understand endpoints based on your use case and requirements.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Chat Mockup */}
            <div className="bg-zinc-800 p-6">
              <div className="text-[10px] font-semibold text-zinc-500 tracking-wider mb-3">
                DocGether AI Help:
              </div>
              <p className="text-white text-lg mb-6">
                &ldquo;Explain how to use the /orders endpoint with a nested filter.&rdquo;
              </p>
              <div className="bg-zinc-700/50 p-4 space-y-2">
                <div className="h-2 bg-zinc-600 rounded w-full"></div>
                <div className="h-2 bg-zinc-600 rounded w-5/6"></div>
                <div className="h-2 bg-zinc-600 rounded w-4/6"></div>
                <div className="h-2 bg-zinc-900 rounded w-1/3 mt-4"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 text-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-zinc-900 leading-tight tracking-tight mb-6">
            READY TO DOCUMENT<br />YOUR API?
          </h2>
          <p className="text-zinc-500 text-base sm:text-lg mb-10 max-w-xl mx-auto">
            Join thousands of developers who trust DocGether for their API documentation needs. Sign up for free and start sharing your specs today.
          </p>
          <button
            onClick={onLoginClick}
            className="bg-zinc-900 text-white px-8 py-4 text-xs font-semibold tracking-wider hover:bg-zinc-800 transition-colors inline-flex items-center gap-2"
          >
            GET STARTED FOR FREE
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-zinc-50 border-t border-zinc-200 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <img src="/docgether.png" alt="DocGether" className="h-5 sm:h-6 w-auto" />
              <span className="text-xs sm:text-sm text-zinc-500">
                © {new Date().getFullYear()} DocGether. All rights reserved.
              </span>
            </div>
            <div className="flex items-center gap-4 sm:gap-6">
              <button onClick={() => onNavigate?.("privacy")} className="text-xs sm:text-sm text-zinc-500 hover:text-zinc-900 transition-colors">
                Privacy
              </button>
              <button onClick={() => onNavigate?.("terms")} className="text-xs sm:text-sm text-zinc-500 hover:text-zinc-900 transition-colors">
                Terms
              </button>
              <button onClick={() => onNavigate?.("contact")} className="text-xs sm:text-sm text-zinc-500 hover:text-zinc-900 transition-colors">
                Contact
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
